import React from 'react';

/**
 * OmidSessionsPanel — each OMID measurement session reconstructed from its
 * beacons, flagging incomplete viewability (impression with no geometry, session
 * with no impression). Detection alone can't tell you viewability was measured
 * end-to-end; this can. Shared by the Lifecycle and Slots tabs.
 *
 * sessions — omidSessions from resolveGraphSource (phases is a Set; api/lifecycle.js hydrates it)
 * local    — true when the browser reconstructed them (the server sent none)
 */

const TONE = {
  fail: { fg: 'var(--error-text)', bg: 'var(--error-bg)' },
  warn: { fg: 'var(--warn-text)', bg: 'var(--warn-bg)' },
  pass: { fg: 'var(--success-text)', bg: 'var(--success-bg)' },
};
const ORDER = ['sessionStart', 'impression', 'geometryChange', 'video', 'sessionFinish'];

export default function OmidSessionsPanel({ sessions, local = false }) {
  if (!Array.isArray(sessions) || sessions.length === 0) return null;
  const broken = sessions.filter((s) => s.status === 'fail').length;
  return (
    <div data-testid="omid-sessions" style={{ marginBottom: 14, border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 600, letterSpacing: 0.3, color: 'var(--muted)', padding: '6px 12px', background: 'var(--surface)', borderBottom: '1px solid var(--line)' }}>
        <span>OMID VIEWABILITY · {sessions.length} session{sessions.length !== 1 ? 's' : ''} · {broken} broken</span>
        {local && (
          <span
            title="The server did not return OMID sessions for this response, so this panel was reconstructed in the browser. It may differ from a server-side sign-off report."
            style={{ fontSize: 11, fontWeight: 400, color: 'var(--warn-text)', border: '1px solid var(--warn-border)', borderRadius: 4, padding: '1px 6px' }}
          >
            computed locally
          </span>
        )}
      </div>
      {sessions.map((s) => {
        const tone = TONE[s.status] || { fg: 'var(--muted)', bg: 'var(--surface)' };
        const phases = s.phases && typeof s.phases.has === 'function' ? s.phases : new Set(s.phases || []);
        const issues = Array.isArray(s.issues) ? s.issues : [];
        const seen = ORDER.filter((p) => phases.has(p));
        return (
          <div key={s.key}
               title={issues.length ? issues.map((i) => `⚑ ${i.message}`).join('\n') : 'Viewability measured end-to-end'}
               style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 6, columnGap: 12, padding: '8px 12px', fontSize: 12, background: tone.bg, borderTop: '1px solid var(--line)' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: tone.fg, flexShrink: 0 }} />
            <span style={{ fontWeight: 600, minWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 240 }}>
              {s.iu || s.key}
            </span>
            <span style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>
              {seen.join(' → ') || 'measurement'}
            </span>
            {s.percentInViewMax != null && (
              <span style={{ whiteSpace: 'nowrap' }} title="Max percentage in view reported by geometry beacons">
                max <b style={{ color: tone.fg }}>{s.percentInViewMax}%</b> in view
              </span>
            )}
            <span style={{ marginLeft: 'auto', color: tone.fg, fontWeight: 700, whiteSpace: 'nowrap' }}>
              {s.status === 'pass' ? '✓ complete' : s.status === 'warn' ? `⚠ ${issues.length} issue${issues.length !== 1 ? 's' : ''}` : '✗ incomplete'}
            </span>
          </div>
        );
      })}
    </div>
  );
}
